import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const ONE_MINUTE = 60000;

/**
 * Returns whether or not user interactions have been idle past given threshold.
 * @param idleThresholdMinutes - positive integer in minutes, default 15 min (minimum value = 2 min)
 * @param warningThresholdMinutes - positive integer in minutes, default 10 min
 *   warning is always false when warningThresholdMinutes <= 0 or >= idleThresholdMinutes
 * @param startTrigger - 'now' | 'later', default 'now' will start timer immediately, 'later' will not start timer
 * @returns [overIdleThreshold: boolean, overWarningThreshold: boolean]
 */
export function useDetectIdleView(
  idleThresholdMinutes = 15,
  warningThresholdMinutes = 10,
  startTrigger: 'now' | 'later' = 'now'
) {
  const [overIdleThreshold, setOverIdleThreshold] = useState(false);
  const [overWarningThreshold, setOverWarningThreshold] = useState(false);
  const timerRef = useRef<number | undefined>();
  const timeoutMinutes = useRef(0);

  const safeIdleThreshold = useMemo(
    // minimum threshold is 2 minute
    () => (idleThresholdMinutes >= 2 ? idleThresholdMinutes : 2),
    [idleThresholdMinutes]
  );
  const safeWarnThreshold = useMemo(
    // disable warning threshold if not a positive integer below idle threshold
    () =>
      warningThresholdMinutes <= 0 || warningThresholdMinutes >= idleThresholdMinutes
        ? null
        : warningThresholdMinutes,
    [idleThresholdMinutes, warningThresholdMinutes]
  );

  const resetTimerAndFlags = useCallback(() => {
    timeoutMinutes.current = 0;
    setOverIdleThreshold(false);
    setOverWarningThreshold(false);
  }, []);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

    const processEvent: EventListener = () => {
      resetTimerAndFlags();
    };

    events.forEach((evt) => {
      document.addEventListener(evt, processEvent);
    });

    return () => {
      events.forEach((evt) => {
        document.removeEventListener(evt, processEvent);
      });
    };
  }, [resetTimerAndFlags]);

  useEffect(() => {
    resetTimerAndFlags();

    timerRef.current =
      startTrigger === 'now'
        ? window.setInterval(() => {
            timeoutMinutes.current = timeoutMinutes.current + 1;
            if (safeWarnThreshold && timeoutMinutes.current >= safeWarnThreshold) {
              setOverWarningThreshold(true);
            }
            if (timeoutMinutes.current >= safeIdleThreshold) {
              setOverIdleThreshold(true);
            }
          }, ONE_MINUTE)
        : undefined;

    return () => {
      window.clearTimeout(timerRef.current);
    };
  }, [startTrigger, safeIdleThreshold, safeWarnThreshold, resetTimerAndFlags]);

  return [overIdleThreshold, overWarningThreshold];
}
